import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {env} from "../../environments/environment";
import {LandingFaqItem, LandingHowTo} from "../interfaces/landing-page";
import {Template} from "../interfaces/template";

export interface SeoPageMeta {
    h1?: string;
    title?: string;
    og_title?: string;
    description?: string;
    og_description?: string;
    keywords?: string;
}

export interface SeoBreadcrumbItem {
    name: string;
    path: string;
}

export interface SeoPageConfig {
    path: string;
    image?: string;
    imageAlt?: string;
    type?: 'website' | 'article';
    robots?: string;
    applicationCategory?: string;
    faq?: LandingFaqItem[];
    breadcrumb?: SeoBreadcrumbItem[];
    howTo?: LandingHowTo | null;
}

@Injectable({providedIn: 'root'})
export class SeoService {
    constructor(
        private title: Title,
        private meta: Meta,
        @Inject(DOCUMENT) private document: Document,
    ) {
    }

    applyTemplate(template: Template, config: SeoPageConfig): void {
        this.applyPage(template?.data?.page, config);
    }

    applyPage(page: SeoPageMeta | undefined, config: SeoPageConfig): void {
        const canonicalUrl = this.absoluteUrl(config.path);
        const pageTitle = page?.title || 'MotorDesk';
        const ogTitle = page?.og_title || pageTitle;
        const description = page?.description || '';
        const ogDescription = page?.og_description || description;
        const imageUrl = this.absoluteUrl(config.image || '/assets/start-page/lk-screen.png');
        const imageAlt = config.imageAlt || page?.h1 || pageTitle;
        const robots = config.robots || 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

        this.title.setTitle(pageTitle);
        this.meta.updateTag({name: 'description', content: description});
        this.meta.updateTag({name: 'keywords', content: page?.keywords || ''});
        this.meta.updateTag({name: 'robots', content: robots});
        this.meta.updateTag({name: 'theme-color', content: '#111a22'});

        this.meta.updateTag({property: 'og:type', content: config.type || 'website'});
        this.meta.updateTag({property: 'og:locale', content: 'ru_RU'});
        this.meta.updateTag({property: 'og:site_name', content: 'MotorDesk'});
        this.meta.updateTag({property: 'og:title', content: ogTitle});
        this.meta.updateTag({property: 'og:description', content: ogDescription});
        this.meta.updateTag({property: 'og:url', content: canonicalUrl});
        this.meta.updateTag({property: 'og:image', content: imageUrl});
        this.meta.updateTag({property: 'og:image:alt', content: imageAlt});

        this.meta.updateTag({name: 'twitter:card', content: 'summary_large_image'});
        this.meta.updateTag({name: 'twitter:title', content: ogTitle});
        this.meta.updateTag({name: 'twitter:description', content: ogDescription});
        this.meta.updateTag({name: 'twitter:image', content: imageUrl});
        this.meta.updateTag({name: 'twitter:image:alt', content: imageAlt});

        this.updateCanonical(canonicalUrl);
        this.renderJsonLd([
            this.organizationSchema(),
            this.websiteSchema(),
            this.webPageSchema(pageTitle, description, canonicalUrl, imageUrl),
            this.softwareApplicationSchema(description, canonicalUrl, config.applicationCategory),
            this.breadcrumbSchema(config.breadcrumb || []),
            ...(config.faq?.length ? [this.faqSchema(config.faq)] : []),
            ...(config.howTo?.steps?.length ? [this.howToSchema(config.howTo, canonicalUrl)] : []),
        ]);
    }

    clearJsonLd(): void {
        this.document.head.querySelectorAll('script[data-seo-json-ld="true"]').forEach((item) => item.remove());
    }

    private renderJsonLd(items: unknown[]): void {
        this.clearJsonLd();

        items.filter(Boolean).forEach((item) => {
            const script = this.document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-seo-json-ld', 'true');
            script.text = JSON.stringify(item);
            this.document.head.appendChild(script);
        });
    }

    private updateCanonical(url: string): void {
        let link = this.document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!link) {
            link = this.document.createElement('link');
            link.rel = 'canonical';
            this.document.head.appendChild(link);
        }

        link.href = url;
    }

    private absoluteUrl(path: string): string {
        if (!path) {
            return env.URL_FRONT;
        }

        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        if (path === '/') {
            return env.URL_FRONT;
        }

        return `${env.URL_FRONT}${path}`;
    }

    private organizationSchema() {
        return {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'MotorDesk',
            url: env.URL_FRONT,
            email: env.CONTACT_EMAIL,
        };
    }

    private websiteSchema() {
        return {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'MotorDesk',
            url: env.URL_FRONT,
            inLanguage: 'ru-RU',
        };
    }

    private webPageSchema(name: string, description: string, url: string, imageUrl: string) {
        return {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name,
            description,
            url,
            inLanguage: 'ru-RU',
            isPartOf: {
                '@type': 'WebSite',
                name: 'MotorDesk',
                url: env.URL_FRONT,
            },
            primaryImageOfPage: imageUrl,
            about: {
                '@type': 'Thing',
                name: 'Учёт автомобиля',
            },
        };
    }

    private softwareApplicationSchema(description: string, url: string, applicationCategory?: string) {
        return {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'MotorDesk',
            applicationCategory: applicationCategory || 'BusinessApplication',
            operatingSystem: 'Web',
            url,
            description,
            isAccessibleForFree: true,
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'RUB',
                availability: 'https://schema.org/InStock',
            },
            publisher: {
                '@type': 'Organization',
                name: 'MotorDesk',
                url: env.URL_FRONT,
            },
        };
    }

    private breadcrumbSchema(items: SeoBreadcrumbItem[]) {
        const normalized = items.filter((item) => !!item?.name).map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: this.absoluteUrl(item.path),
        }));

        if (!normalized.length) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: normalized,
        };
    }

    private faqSchema(items: LandingFaqItem[]) {
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            })),
        };
    }

    private howToSchema(item: LandingHowTo, url: string) {
        return {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: item.name,
            url,
            inLanguage: 'ru-RU',
            step: item.steps.map((step, index) => ({
                '@type': 'HowToStep',
                position: index + 1,
                name: `Шаг ${index + 1}`,
                text: step,
            })),
        };
    }
}
